import React, { useState, useCallback } from "react";
import { PDFDownloadLink } from "@react-pdf/renderer";
import InvoicePDF from "./InvoicePDF";
import { InvoiceFormData } from "./types";
import { computeInvoice, fmt, numberToWords } from "../../utils/invoiceCalculations";
import "./GenerateInvoice.scss";

const today = new Date();
const defaultDate = `${String(today.getDate()).padStart(2, "0")}-${String(today.getMonth() + 1).padStart(2, "0")}-${today.getFullYear()}`;

const DEFAULTS: InvoiceFormData = {
  invoiceNo: "XPLR/19/26-27",
  invoiceDate: defaultDate,
  placeOfSupply: "Kerala(32)",
  grRrNo: "",
  transport: "",
  vehicleNo: "",
  station: "",
  eWayBillNo: "",
  customerName: "",
  consumerNumber: "",
  addressLine1: "",
  addressLine2: "",
  phoneNumber: "",
  clientPan: "",
  gstin: "",
  totalGrandAmount: "",
  systemDescription: "SPG SYSTEM 3.51 kW ONGRID STRING\n(SO No: 65344447)",
};

const PLACEHOLDERS: Partial<InvoiceFormData> = {
  customerName: "e.g. ANILKUMAR B",
  consumerNumber: "e.g. CN1146321011954",
  addressLine1: "Street, City",
  phoneNumber: "+919605094978",
  clientPan: "e.g. AGLPA5224D",
  gstin: "Optional",
  totalGrandAmount: "e.g. 215000",
};

type SectionKey = "invoice" | "customer" | "financial";

export const GenerateInvoice: React.FC = () => {
  const [form, setForm] = useState<InvoiceFormData>(DEFAULTS);
  const [openSection, setOpenSection] = useState<SectionKey>("invoice");
  const [pdfReady, setPdfReady] = useState(false);

  const handleChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      const { name, value } = e.target;
      setForm((prev) => ({ ...prev, [name]: value }));
      setPdfReady(false);
    },
    []
  );

  const handleGenerate = (e: React.FormEvent) => {
    e.preventDefault();
    setPdfReady(true);
  };

  const grandAmt = Number(form.totalGrandAmount) || 0;
  const calc = grandAmt > 0 ? computeInvoice(grandAmt) : null;
  const amountWords = calc ? numberToWords(calc.grandTotal) : "";


  return (
    <div className="giv-root">
      {/* Left Panel – Form */}
      <div className="giv-form-panel">
        <div className="giv-form-header">
          <img
            src={`${process.env.PUBLIC_URL}/xplor-logo.png`}
            alt="Xplor Infrastructures"
            className="giv-header-logo"
          />
          <div>
            <h1 className="giv-form-title">GST Tax Invoice</h1>
            <p className="giv-form-subtitle">Xplor Infrastructures • Indian A4 Format</p>
          </div>
        </div>

        <form onSubmit={handleGenerate} className="giv-form">

          {/* ── Section 1: Invoice Details ─────────────────────── */}
          <div className={`giv-section ${openSection === "invoice" ? "open" : ""}`}>
            <button type="button" className="giv-section-toggle" onClick={() => setOpenSection("invoice")}>
              <span className="giv-section-num">01</span>
              <span className="giv-section-label">Invoice Details</span>
              <span className="giv-section-arrow">▾</span>
            </button>
            <div className="giv-section-body">
              <div className="giv-grid-2">
                <div className="giv-field">
                  <label>Invoice No.</label>
                  <input name="invoiceNo" value={form.invoiceNo} onChange={handleChange} />
                </div>
                <div className="giv-field">
                  <label>Date (DD-MM-YYYY)</label>
                  <input name="invoiceDate" value={form.invoiceDate} onChange={handleChange} />
                </div>
                <div className="giv-field">
                  <label>Place of Supply</label>
                  <input name="placeOfSupply" value={form.placeOfSupply} onChange={handleChange} />
                </div>
                <div className="giv-field">
                  <label>GR/RR No.</label>
                  <input name="grRrNo" value={form.grRrNo} onChange={handleChange} placeholder="Optional" />
                </div>
                <div className="giv-field">
                  <label>Transport</label>
                  <input name="transport" value={form.transport} onChange={handleChange} placeholder="Optional" />
                </div>
                <div className="giv-field">
                  <label>Vehicle No.</label>
                  <input name="vehicleNo" value={form.vehicleNo} onChange={handleChange} placeholder="Optional" />
                </div>
                <div className="giv-field">
                  <label>Station</label>
                  <input name="station" value={form.station} onChange={handleChange} placeholder="Optional" />
                </div>
                <div className="giv-field">
                  <label>E-Way Bill No.</label>
                  <input name="eWayBillNo" value={form.eWayBillNo} onChange={handleChange} placeholder="Optional" />
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 2: Customer Details ────────────────────── */}
          <div className={`giv-section ${openSection === "customer" ? "open" : ""}`}>
            <button type="button" className="giv-section-toggle" onClick={() => setOpenSection("customer")}>
              <span className="giv-section-num">02</span>
              <span className="giv-section-label">Customer Details</span>
              <span className="giv-section-arrow">▾</span>
            </button>
            <div className="giv-section-body">
              <div className="giv-grid-2">
                <div className="giv-field giv-span-2">
                  <label>Customer Name <span className="req">*</span></label>
                  <input required name="customerName" value={form.customerName} onChange={handleChange} placeholder={PLACEHOLDERS.customerName} />
                </div>
                <div className="giv-field">
                  <label>Consumer Number</label>
                  <input name="consumerNumber" value={form.consumerNumber} onChange={handleChange} placeholder={PLACEHOLDERS.consumerNumber} />
                </div>
                <div className="giv-field">
                  <label>Phone Number <span className="req">*</span></label>
                  <input required name="phoneNumber" value={form.phoneNumber} onChange={handleChange} placeholder={PLACEHOLDERS.phoneNumber} />
                </div>
                <div className="giv-field giv-span-2">
                  <label>Address Line 1 <span className="req">*</span></label>
                  <input required name="addressLine1" value={form.addressLine1} onChange={handleChange} placeholder={PLACEHOLDERS.addressLine1} />
                </div>
                <div className="giv-field giv-span-2">
                  <label>Address Line 2</label>
                  <input name="addressLine2" value={form.addressLine2} onChange={handleChange} placeholder="Optional" />
                </div>
                <div className="giv-field">
                  <label>Client PAN <span className="req">*</span></label>
                  <input required name="clientPan" value={form.clientPan} onChange={handleChange} placeholder={PLACEHOLDERS.clientPan} />
                </div>
                <div className="giv-field">
                  <label>GSTIN</label>
                  <input name="gstin" value={form.gstin} onChange={handleChange} placeholder={PLACEHOLDERS.gstin} />
                </div>
              </div>
            </div>
          </div>

          {/* ── Section 3: Financial Details ───────────────────── */}
          <div className={`giv-section ${openSection === "financial" ? "open" : ""}`}>
            <button type="button" className="giv-section-toggle" onClick={() => setOpenSection("financial")}>
              <span className="giv-section-num">03</span>
              <span className="giv-section-label">Financial Details</span>
              <span className="giv-section-arrow">▾</span>
            </button>
            <div className="giv-section-body">
              <div className="giv-field" style={{ marginBottom: "16px" }}>
                <label>Total Grand Amount (₹) <span className="req">*</span></label>
                <input
                  required
                  type="number"
                  name="totalGrandAmount"
                  value={form.totalGrandAmount}
                  onChange={handleChange}
                  placeholder={String(PLACEHOLDERS.totalGrandAmount ?? "")}
                  className="giv-amount-input"
                />
              </div>
              <div className="giv-field">
                <label>System Description</label>
                <textarea
                  name="systemDescription"
                  value={form.systemDescription}
                  onChange={handleChange}
                  rows={3}
                />
              </div>

              {/* Live Calculation Preview */}
              {calc && (
                <div className="giv-calc-preview">
                  <div className="giv-calc-title">Live Tax Breakdown</div>
                  <div className="giv-calc-grid">
                    <div className="giv-calc-row">
                      <span>System Base (90%)</span>
                      <span>₹{fmt(calc.systemBase)}</span>
                    </div>
                    <div className="giv-calc-row">
                      <span>Service Base (10%)</span>
                      <span>₹{fmt(calc.serviceBase)}</span>
                    </div>
                    <div className="giv-calc-row divider">
                      <span>CGST</span>
                      <span>₹{fmt(calc.cgstOutput)}</span>
                    </div>
                    <div className="giv-calc-row">
                      <span>SGST</span>
                      <span>₹{fmt(calc.sgstOutput)}</span>
                    </div>
                    <div className="giv-calc-row total">
                      <span>Grand Total</span>
                      <span>₹{fmt(calc.grandTotal)}</span>
                    </div>
                    <div className="giv-calc-words">{amountWords}</div>
                  </div>
                </div>
              )}
            </div>
          </div>

          <button type="submit" className="giv-generate-btn" id="btn-generate-invoice">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
              <path d="M12 5v14M5 12l7 7 7-7" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            Generate Invoice PDF
          </button>
        </form>
      </div>

      {/* Right Panel – Preview & Download */}
      <div className="giv-preview-panel">
        <div className="giv-preview-header">
          <h2>Invoice Preview</h2>
          <p className="giv-preview-subtitle">Fill the form and click Generate</p>
        </div>

        {!pdfReady && (
          <div className="giv-placeholder">
            <div className="giv-placeholder-icon">
              <img
                src={`${process.env.PUBLIC_URL}/xplor-logo.png`}
                alt="Xplor Infrastructures"
                className="giv-placeholder-logo"
              />
            </div>
            <p>Your invoice will appear here</p>
          </div>
        )}

        {pdfReady && (
          <div className="giv-pdf-ready">
            <div className="giv-ready-icon">✓</div>
            <h3>Invoice Ready!</h3>
            <p className="giv-ready-subtitle">
              {form.customerName} — ₹{fmt(calc?.grandTotal || 0)}
            </p>
            <p className="giv-ready-words">{amountWords}</p>

            <div className="giv-invoice-summary">
              <div className="giv-sum-row"><span>Invoice No.</span><strong>{form.invoiceNo}</strong></div>
              <div className="giv-sum-row"><span>Date</span><strong>{form.invoiceDate}</strong></div>
              <div className="giv-sum-row"><span>Customer</span><strong>{form.customerName}</strong></div>
              <div className="giv-sum-row"><span>Place of Supply</span><strong>{form.placeOfSupply}</strong></div>
              <div className="giv-sum-row"><span>System Base</span><strong>₹{fmt(calc?.systemBase || 0)}</strong></div>
              <div className="giv-sum-row"><span>Service Base</span><strong>₹{fmt(calc?.serviceBase || 0)}</strong></div>
              <div className="giv-sum-row"><span>CGST</span><strong>₹{fmt(calc?.cgstOutput || 0)}</strong></div>
              <div className="giv-sum-row"><span>SGST</span><strong>₹{fmt(calc?.sgstOutput || 0)}</strong></div>
              <div className="giv-sum-row total"><span>Grand Total</span><strong>₹{fmt(calc?.grandTotal || 0)}</strong></div>
            </div>

            <PDFDownloadLink
              document={<InvoicePDF data={form} />}
              fileName={`Invoice-${form.invoiceNo.replace(/\//g, "-")}-${form.customerName.replace(/\s+/g, "_")}.pdf`}
              className="giv-download-btn"
              id="btn-download-pdf"
            >
              {({ loading }) =>
                loading ? (
                  <span>Generating PDF…</span>
                ) : (
                  <>
                    <svg width="18" height="18" viewBox="0 0 24 24" fill="none">
                      <path d="M21 15v4a2 2 0 01-2 2H5a2 2 0 01-2-2v-4M7 10l5 5 5-5M12 15V3" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    Download PDF Invoice
                  </>
                )
              }
            </PDFDownloadLink>

            <button
              className="giv-reset-btn"
              onClick={() => { setPdfReady(false); }}
              id="btn-edit-invoice"
            >
              ← Edit Invoice
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default GenerateInvoice;
